
import { computed, effect, inject, Injectable, signal, untracked } from '@angular/core';
import { ActivityBin, ChatFilters, Highlight, TwitchComment } from '../models/twitch.model';
import { LibraryService } from './library.service.js';
import { MarkersService } from './markers.service.js';
import { SyncService } from './sync.service.js';
import { checkDemoMode } from './demo.service.js';
import { isTranscriptSource, SpeechMode, TRANSCRIPT_CATEGORY_KEYS, TranscriptCategory } from '../models/transcript.model.js';
import { NEW_DOC_REV, OpenDecision, RemoteState, decideOnOpen, syncSignature, withLocalChat } from '../utils/sync-reconcile.js';
import { TimeRegion, isVisibleInRegion, overlapsRegion, parseRegion } from '../utils/region.js';
import { VodChapter } from '../utils/vod-chapters.js';

/**
 * What happened to the open document — UndoHistoryService (onDocumentEvent) drops or prunes
 * a VOD's undo history on these. 'reset': resetState, the next VOD is built from scratch;
 * 'loaded': loadStateObject (origin 'cloud' = another device's copy replaced ours);
 * 'replaced': a VOD that isn't open got a cloud copy; 'results-replaced': a newer AI / «Речь»
 * run replaced a result group (older undo steps must not bring the stale results back).
 */
export type DocumentEvent =
  | { kind: 'reset' }
  | { kind: 'loaded'; videoId: string; origin: 'local' | 'cloud' }
  | { kind: 'replaced'; videoId: string }
  | {
      kind: 'results-replaced'; videoId: string; group: 'search' | 'transcript-ai' | 'transcript-search';
      scope: TimeRegion | null; before: readonly Highlight[]; after: readonly Highlight[];
    };

/** Result of resolving which copy of a VOD to open — see resolveProjectForOpen. */
export interface OpenedProject {
  videoId: string;
  state: SavedState | null;
  decision: OpenDecision | null; // null = cloud not consulted (not Pro / unreachable)
  local: SavedState | null;
  remote: RemoteState | null;
}

// For caching AI analysis results
export interface AnalysisCacheData {
  description: string;
  sentiment: string;
}

// For caching user-driven highlight state changes
export interface HighlightState {
  isPinned: boolean;
  isDeleted: boolean;
  clipStartTime: number;
  clipEndTime: number;
  isStartTimeManuallySet: boolean;
}

export interface SavedState {
  fileName?: string;
  activityBins?: ActivityBin[];
  chatComments?: TwitchComment[]; // Added raw comments storage
  highlights?: Highlight[];
  maxActivity?: number;
  streamerName?: string | null;
  videoTitle?: string | null;
  videoGame?: string | null;
  videoId?: string | number | null;
  streamCreatedAt?: string | null;
  peakMessageThreshold?: number;
  rewindOffsetSeconds?: number;
  isAnalyzed?: boolean;
  currentHighlightIndex?: number;
  sortOrder?: 'time' | 'count';
  analysisCache?: [number, AnalysisCacheData][];
  highlightStateCache?: [number, HighlightState][];
  isChatOverlayEnabled?: boolean;
  // 'auto' picks 'side' on browsers that enforce obscured-iframe (Chrome family)
  // and 'overlay' elsewhere (Firefox/Safari fallback to inside-iframe visibility).
  chatLayoutPreference?: 'auto' | 'overlay' | 'side';
  interfaceScale?: number;
  heatmapScale?: 'log' | 'linear';
  filterSettings?: ChatFilters;

  // «Область интереса» (heatmap IN/OUT). Per VOD; syncs as a ride-along of the next real
  // edit — deliberately NOT part of workFingerprint/syncSignature (moving the region
  // alone must neither push nor conflict).
  analysisRegion?: TimeRegion | null;
  analysisRegionEnabled?: boolean;

  // Auto-resume support: if a chat download was interrupted (VOD switched mid-flight,
  // tab closed, error), we persist the partial chatComments + cursor and pick up where
  // we stopped on the next loadFromUrl for this VOD. `chatDownloadComplete` is set true
  // only when the GQL loop terminates naturally (`pageInfo.hasNextPage === false`); a
  // missing/false flag on a state with non-empty chatComments triggers resume mode.
  chatDownloadComplete?: boolean;
  chatDownloadCursor?: string | null;

  // Live-stream support: was this VOD live at the last /get-vod-status check?
  // Persisted as a hint so reopen can show the "🔴 В эфире" badge optimistically
  // before the next status check resolves; the real source of truth is always the
  // fresh status response. liveStreamId is the Twitch stream.id captured from the
  // VOD's stream_id field — used by the backend to confirm "live for THIS VOD".
  isLive?: boolean;
  liveStreamId?: string | null;

  // Cloud Sync bookkeeping (local only — stripped from the cloud payload). See
  // utils/sync-reconcile.ts: syncRev = cloud `rev` this copy is based on
  // (undefined = legacy copy, NEW_DOC_REV = created here from scratch);
  // syncDirty = has edits the cloud hasn't confirmed yet.
  syncRev?: string;
  syncDirty?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class TwitchChatStateService {
  private readonly SESSION_STORAGE_KEY = 'twitch-analyzer-session';
  private readonly SEARCH_HISTORY_KEY = 'twitch-analyzer-search-history';
  private readonly SEARCH_HISTORY_MAX = 10;
  private libraryService = inject(LibraryService);
  private syncService = inject(SyncService);
  private markersService = inject(MarkersService);

  // Cloud Sync bookkeeping for the open VOD — persisted with it (SavedState.syncRev
  // / syncDirty), never part of the pushed content. Signals so the IDB autosave
  // picks up changes made after a push lands.
  syncRev = signal<string | undefined>(NEW_DOC_REV);
  syncDirty = signal<boolean>(false);
  // VOD whose next push-effect emission is the freshly loaded copy itself — record
  // it as the baseline instead of pushing it. Opening a VOD is not an edit.
  private syncBaselineFor: string | null = null;
  private lastSyncSigByVideo = new Map<string, string>();
  // VOD restored from the last session at boot, not yet checked against the cloud
  // (sign-in is async). Its pushes are held until reconcileOpenVodWithCloud('boot').
  private reconcileHoldFor: string | null = null;
  private editedDuringHold = false;
  private lastReconcileAt = 0;
  // Undo history hook — UndoHistoryService sets it (injecting that service here would be
  // circular). Same idiom as syncService.onStatePushed.
  onDocumentEvent: ((e: DocumentEvent) => void) | null = null;


  // File & Loading State
  fileName = signal<string>('');
  isLoading = signal<boolean>(false);
  loadingMessage = signal<string>('');
  error = signal<string>('');
  // Neutral info message channel — used for non-error UX feedback (e.g. "Deep
  // re-run found no new moments"). Shown under the error banner with blue
  // styling instead of red, so it doesn't read as a failure.
  infoMessage = signal<string>('');
  // Long-running operation progress for the sidebar footer.
  //   - `percent` (0..100) drives the bar width; if undefined, the bar runs
  //     indeterminate (gradient slide). For chat download we derive it from
  //     the latest comment's contentOffsetSeconds vs the VOD's lengthSeconds —
  //     same trick TwitchDownloaderWPF uses since GQL doesn't tell us the
  //     total comment count up front.
  //   - `detail` is a free-form right-side text (e.g. "12 345 сообщений").
  //   - `startedAt` is performance.now() at op start; the component appends
  //     elapsed mm:ss after the detail.
  progressInfo = signal<{ label: string; percent?: number; detail?: string; startedAt: number } | null>(null);
  isProcessed = signal<boolean>(false);

  // True while the streaming chat download is in progress. Used to:
  // (a) keep the loading message active even though `isProcessed` flipped early in onMeta,
  // (b) gate AI Quick to exclude the still-growing boundary cluster (last cluster's bins
  //     can absorb more messages until the download advances past them),
  // (c) suppress idle-only persistence while data is mutating rapidly.
  isChatDownloading = signal<boolean>(false);

  // True only during the parallel-window phase of a fresh download (NOT for sequential
  // resumes / live-poll continuations). Comments arrive out-of-order from N concurrent
  // workers, so the single-boundary-cluster heuristic in `quickBoundaryHighlight` no
  // longer applies — every window has a growing front. AI Quick is gated entirely
  // until the parallel phase finishes (`analyzableQuickCount` returns 0). After the
  // orchestrator merges + dedups the windows, this flips false and the live-poll path
  // (if any) keeps the prior single-boundary semantics intact.
  parallelDownloadActive = signal<boolean>(false);

  // Auto-resume markers — kept on state so saveStateToLocalStorage picks them up via
  // the SavedState contract. complete=undefined ⇒ legacy state (treat as complete to
  // avoid spurious resumes on already-finished projects); complete=false ⇒ partial,
  // resume on next open; complete=true ⇒ done. Cursor is the last GQL cursor we
  // received; null means we haven't fetched any pages yet (or the download already
  // wrapped up naturally).
  chatDownloadComplete = signal<boolean>(true);
  chatDownloadCursor = signal<string | null>(null);

  // Live-stream state. `isLive` flips true after a /get-vod-status response
  // confirms the VOD's stream_id matches the current live-stream id; flips false
  // when the live-poll loop detects offline (Helix /streams empty). `liveStreamId`
  // is the matching Twitch stream id — kept around so the backend's per-VOD live
  // check can compare against it on reopen. `lastLivePollAt` is performance.now()
  // of the last successful poll-tick, used by the footer to show "обновлено N сек назад".
  isLive = signal<boolean>(false);
  liveStreamId = signal<string | null>(null);
  lastLivePollAt = signal<number | null>(null);
  // Latest VOD duration reported by /get-vod-status while live (now - liveStream.started_at,
  // floored, but never below Helix's video.duration). Used by app.component's duration
  // effect to extend videoDurationSec authoritatively — Twitch player.getDuration() lags
  // the live stream by seconds-to-minutes and only refreshes lazily on user scrub.
  liveDurationSec = signal<number>(0);

  // Per-operation AI flags so Quick (peaks) and Deep can run in parallel — each
  // button only disables itself, retry pills and spinners key off their own flag,
  // and the global footer derives a combined message via `globalLoadingMessage`.
  isAnalyzingQuick = signal<boolean>(false);
  isAnalyzingDeep = signal<boolean>(false);
  isAnalyzingSearch = signal<boolean>(false);
  quickRetryInfo = signal<{ attempt: number; maxAttempts: number; remainingSec: number; reason: string } | null>(null);
  deepRetryInfo = signal<{ attempt: number; maxAttempts: number; remainingSec: number; reason: string } | null>(null);
  searchRetryInfo = signal<{ attempt: number; maxAttempts: number; remainingSec: number; reason: string } | null>(null);

  // Aggregate flags for "anything in flight" — used to disable URL/CSV inputs and
  // drive the sidebar status footer regardless of which operation is running.
  isAnalyzingAny = computed(() => this.isAnalyzingQuick() || this.isAnalyzingDeep() || this.isAnalyzingSearch());
  globalIsLoading = computed(() => this.isLoading() || this.isAnalyzingAny());
  globalLoadingMessage = computed(() => {
    if (this.isLoading()) return this.loadingMessage();
    const q = this.isAnalyzingQuick();
    const d = this.isAnalyzingDeep();
    const s = this.isAnalyzingSearch();
    if (q && d) return 'Анализ ИИ: пики + глубокий...';
    if (q) return 'Анализирую ключевые моменты...';
    if (d) return 'Провожу глубокий анализ чата...';
    if (s) return 'Ищу момент по описанию...';
    return '';
  });

  // VOD Info
  streamerName = signal<string | null>(null);
  videoTitle = signal<string | null>(null);
  videoGame = signal<string | null>(null);
  videoId = signal<string | number | null>(null);
  streamCreatedAt = signal<string | null>(null);

  // Analysis & Data
  activityBins = signal<ActivityBin[]>([]);
  chatComments = signal<TwitchComment[]>([]); // Raw comments
  filteredComments = signal<TwitchComment[]>([]); // Comments after filters applied
  highlights = signal<Highlight[]>([]);
  streamMarkers = signal<Highlight[]>([]);
  // Transient — refetched per session by fetchAndMergeUserClips. Mirrors `streamMarkers`
  // pattern: NOT persisted in SavedState, NOT synced to Firestore (the merged
  // user-clip highlights in highlights[] are). Each device refetches on VOD open
  // (Twitch GQL video.clips, Helix /get-vod-clips fallback).
  userClips = signal<Highlight[]>([]);
  isLoadingClips = signal<boolean>(false);
  // Broadcaster (user_id) of the currently-loaded VOD. Captured from /get-vod-status
  // response. Required only by the Helix fallback of the clips fetch (broadcaster_id).
  // Falls back to the library project's channelId / savedChannels lookup in paths
  // where /get-vod-status isn't called. Reset on VOD change (resetState / loadStateObject).
  broadcasterId = signal<string | null>(null);
  // Streamer's login, from the GQL download (onMeta). Viewers mention the streamer by login, which a
  // localized display name doesn't match (AI Deep marks such mentions «@стример»). Transient like
  // broadcasterId — reset on VOD change; TwitchChatService.streamerLoginForAi falls back to the
  // library channel when it's unknown (VOD opened from saved state).
  streamerLogin = signal<string | null>(null);
  // Transient — Twitch categories (game changes) of a VOD for the heatmap zoom bar, fetched
  // on every open by TwitchChatService (GQL, like clips). Tagged with its videoId instead of
  // being reset on VOD change: a re-download of the same VOD keeps it, another VOD ignores it.
  // NOT in SavedState / sync.
  vodChapters = signal<{ videoId: string; chapters: VodChapter[] } | null>(null);
  // One-shot navigation hint set when a click on a top-clip card in the library
  // triggers a fresh loadFromUrl: stash vod_offset here, app.component consumes
  // it inside Twitch.Player.READY and clears. Cleared eagerly on chat-download
  // error or supersede so a failed clip-click doesn't leak a seek into the
  // next VOD opened.
  pendingSeekAfterLoad = signal<number | null>(null);
  maxActivity = signal<number>(0);
  isAnalyzed = signal<boolean>(false);

  // UI & Settings
  peakMessageThreshold = signal<number>(15);
  rewindOffsetSeconds = signal<number>(15);
  sortOrder = signal<'time' | 'count'>('time');
  // 'speech' = «Речь» tab (admin-only, experimental) — the tab button only renders for admins,
  // and app.component resets the tab to 'all' if a non-admin ever lands on it.
  activeTab = signal<'all' | 'pinned' | 'deleted' | 'search' | 'clips' | 'speech'>('all');
  currentHighlightIndex = signal<number>(0);
  selectedToMerge = signal<Set<number>>(new Set());
  isChatOverlayEnabled = signal<boolean>(true); // UI toggle state
  // Chat layout: 'overlay' = absolute over player (legacy, blocked by Twitch SDK
  // obscured-check in Chrome 2026-05-13+); 'side' = side-by-side column to the
  // right of the player (no bbox overlap, works everywhere); 'auto' = pick
  // based on obscuredEnforcementSupported() — Chrome→side, others→overlay.
  chatLayoutPreference = signal<'auto' | 'overlay' | 'side'>('auto');
  interfaceScale = signal<number>(100); // UI Scale %
  // Heatmap bar height scale. 'log' (default) handles both spike-heavy (xQc-tier) and
  // low-activity streams reasonably — distinguishes 5 vs 11 msgs without crushing 2000-msg
  // outliers. 'linear' is raw count/maxActivity (no clipping) for users who want strict
  // proportionality and accept that one outlier compresses the rest.
  heatmapScale = signal<'log' | 'linear'>('log');

  // Search state
  searchQuery = signal<string>('');           // active KEYWORD query shown in input, drives heatmap overlay
  searchInputValue = signal<string>('');      // text currently typed in the search input (pre-submit)
  searchExactMatch = signal<boolean>(false);  // disables morphology stemming when true
  searchHistory = signal<string[]>([]);       // last N queries (both modes), newest first; persisted separately
  searchBinMatches = signal<Map<number, number>>(new Map()); // binStart → matchCount, powers heatmap overlay (keyword mode only)
  // Ephemeral UI toggle for the ПОИСК tab, not persisted (like activeTab). AI mode
  // does NOT write searchQuery/searchBinMatches — those stay keyword-only so the
  // heatmap amber overlay never shows a stale/irrelevant match map after an AI run.
  searchMode = signal<'keyword' | 'ai'>('keyword');

  // «Речь» tab (admin-only, experimental) — ephemeral UI state, not persisted.
  // segments = Gemini interest segments list; text = transcript panel; ai-search = query over speech.
  speechMode = signal<SpeechMode>('segments');
  speechCategoryFilter = signal<ReadonlySet<TranscriptCategory>>(new Set(TRANSCRIPT_CATEGORY_KEYS));

  filterSettings = signal<ChatFilters>({
    hideSubs: false,
    keywords: [],
    ignoredUsers: []
  });

  // «Область интереса»: regionRange/regionEnabled persist per VOD; regionDraft is the live
  // preview while an edge is being dragged (transient, not in snapshotState — committing
  // on pointerup keeps the IDB autosave from re-serializing the whole state per pointermove).
  // activeRegion is what everything reads: lists, counts, export, and every analysis run.
  regionRange = signal<TimeRegion | null>(null);
  regionEnabled = signal<boolean>(false);
  regionDraft = signal<TimeRegion | null>(null);
  activeRegion = computed<TimeRegion | null>(() =>
    this.regionEnabled() ? (this.regionDraft() ?? this.regionRange()) : null);

  // Caching
  analysisCache = signal<Map<number, AnalysisCacheData>>(new Map());
  highlightStateCache = new Map<number, HighlightState>();

  // True when state was pulled from cloud (analyzed VOD, bins+highlights present)
  // but the raw chat is missing locally — surfaces a "Re-download chat" CTA.
  // Auto-clears once chat is re-fetched (chatComments populated → bins still set →
  // returns false because comments.length > 0).
  needsChatRedownload = computed(() =>
    this.chatComments().length === 0 && this.activityBins().length > 0,
  );

  // CSV Mode specific
  isCsvMode = signal<boolean>(false);
  pendingCsvContent = signal<string | null>(null);
  csvVodId = signal<string>('');
  csvStreamerName = signal<string>('');
  csvVideoTitle = signal<string>('');
  csvVideoGame = signal<string>('');

  // --- Computed Signals ---

  currentHighlight = computed(() => {
    const highlights = this.highlights();
    const index = this.currentHighlightIndex();
    return (index >= 0 && index < highlights.length) ? highlights[index] : null;
  });

  // «Основной» список: обычные моменты + перенесённые результаты поиска и пользовательских клипов.
  // Сырые (непромотнутые) search-* и user-clip не показываются в ВСЕ — только в своих вкладках.
  // With a region active, «Все» / «Поиск» / «Клипы» / «Речь» show only moments touching it;
  // pinned ones (Избранное) always stay. «Избранное» and «Корзина» are never region-filtered.
  allHighlights = computed(() => {
    const region = this.activeRegion();
    return this.highlights().filter(h => {
      if (h.isPinned || h.isDeleted) return false;
      const isOptIn =
        h.source === 'search-keyword' ||
        h.source === 'search-ai' ||
        h.source === 'user-clip' ||
        isTranscriptSource(h.source);
      return (!isOptIn || !!h.isPromoted) && isVisibleInRegion(h, region);
    });
  });
  pinnedHighlights = computed(() => this.highlights().filter(h => h.isPinned && !h.isDeleted));
  deletedHighlights = computed(() => this.highlights().filter(h => h.isDeleted));
  searchHighlights = computed(() => {
    const region = this.activeRegion();
    return this.highlights().filter(h => {
      if (h.isDeleted) return false;
      return (h.source === 'search-keyword' || h.source === 'search-ai') && isVisibleInRegion(h, region);
    });
  });
  // КЛИПЫ-вкладка: всегда показывает все non-deleted user-clip моменты,
  // независимо от isPromoted (промоут — это «также показать в ВСЕ»).
  clipsHighlights = computed(() => {
    const region = this.activeRegion();
    return this.highlights().filter(h => {
      if (h.isDeleted) return false;
      return h.source === 'user-clip' && isVisibleInRegion(h, region);
    });
  });
  // «Речь»: segments mode → Gemini segments filtered by category chips; ai-search mode →
  // query hits over speech. (In text mode the list is replaced by the transcript panel.)
  speechHighlights = computed(() => {
    const mode = this.speechMode();
    const cats = this.speechCategoryFilter();
    const region = this.activeRegion();
    return this.highlights().filter(h => {
      if (h.isDeleted) return false;
      if (!isVisibleInRegion(h, region)) return false;
      if (mode === 'ai-search') return h.source === 'transcript-search';
      return h.source === 'transcript-ai' && (!h.transcriptCategory || cats.has(h.transcriptCategory));
    });
  });

  // Single source of truth for "what gets exported / zipped / sized".
  // Mirrors what's visible in the «Все» + «Избранное» tabs combined: skip trash
  // and skip opt-in results (search / clips / «Речь») that are neither promoted
  // nor pinned — `pinnedHighlights` shows every pinned item regardless of source,
  // so a pinned opt-in item must export too (else «Избранное» lists 12, .bat gets 3).
  // Source-filter gate: hidden marker-sources excluded ONLY for non-pinned items —
  // pinned represents an explicit user decision and survives the filter.
  // Region gate: same rule as the lists — outside the active region only pinned exports.
  exportableHighlights = computed(() => {
    const hidden = this.markersService.hiddenSourceIds();
    const region = this.activeRegion();
    return this.highlights().filter(h => {
      if (h.isDeleted) return false;
      if (!isVisibleInRegion(h, region)) return false;
      const isOptIn =
        h.source === 'search-keyword' ||
        h.source === 'search-ai' ||
        h.source === 'user-clip' ||
        isTranscriptSource(h.source);
      if (isOptIn && !h.isPromoted && !h.isPinned) return false;
      if (h.source === 'stream-marker'
          && !h.isPinned
          && h.markerSourceId
          && hidden.has(h.markerSourceId)) return false;
      return true;
    });
  });

  activeHighlights = computed(() => {
    switch (this.activeTab()) {
      case 'pinned': return this.pinnedHighlights();
      case 'deleted': return this.deletedHighlights();
      case 'search': return this.searchHighlights();
      case 'clips': return this.clipsHighlights();
      case 'speech': return this.speechHighlights();
      default: return this.allHighlights();
    }
  });

  sortedHighlights = computed(() => {
    const highlights = [...this.activeHighlights()];
    const order = this.sortOrder();
    const tab = this.activeTab();

    if (order === 'count') {
      if (tab === 'search') {
        // На вкладке поиска сортируем по числу совпадений (а не по всему трафику окна)
        highlights.sort((a, b) => (b.matchCount ?? 0) - (a.matchCount ?? 0));
      } else if (tab === 'speech') {
        // «Речь»: оценка участка / уверенность AI-поиска
        highlights.sort((a, b) => (b.confidence ?? 0) - (a.confidence ?? 0));
      } else {
        highlights.sort((a, b) => b.messageCount - a.messageCount);
      }
    } else {
      highlights.sort((a, b) => a.startTime - b.startTime);
    }
    return highlights;
  });

  // Peaks AI Quick would send — region-scoped, so «Анализ пиков (N)» and the button's
  // disabled state match what analyzeHighlightsWithAI actually does.
  unanalyzedCount = computed(() => {
    const region = this.activeRegion();
    return this.highlights().filter(h =>
      !h.isAnalyzed && h.source === 'peak' && (!region || overlapsRegion(h.startTime, h.endTime, region))).length;
  });

  // For each non-pinned, non-deleted highlight: the pinned highlight whose clip range
  // contains this one's startTime (or undefined if none). Used by the sidebar card to
  // show a "subsumed by [HH:MM:SS]" badge — solves the user's complaint that extending
  // one moment to 20+ minutes leaves the smaller covered moments cluttering the «Все» tab.
  // Search highlights are excluded both as parent and child — they live in their own tab
  // with separate workflow. Stream markers and AI deep-dive participate symmetrically
  // (any pinned can be parent; any non-pinned can be child).
  containingPinnedByStartTime = computed(() => {
    const map = new Map<number, Highlight>();
    const all = this.highlights();
    const pinnedParents = all.filter(h =>
      h.isPinned
      && !h.isDeleted
      && h.source !== 'search-keyword'
      && h.source !== 'search-ai'
      && !isTranscriptSource(h.source)
    );
    if (pinnedParents.length === 0) return map;
    for (const h of all) {
      if (h.isPinned || h.isDeleted) continue;
      if (h.source === 'search-keyword' || h.source === 'search-ai') continue;
      if (isTranscriptSource(h.source)) continue;
      for (const parent of pinnedParents) {
        if (parent.startTime === h.startTime) continue;
        if (h.startTime >= parent.clipStartTime && h.startTime <= parent.clipEndTime) {
          map.set(h.startTime, parent);
          break; // first match wins; multiple parents are rare and not worth surfacing
        }
      }
    }
    return map;
  });

  // Boundary cluster — the unanalyzed peak with the highest endTime. While the chat
  // is still downloading, this cluster's bins may absorb more messages on the next
  // batch, so we hold off analyzing it until the download front advances past it.
  // Returns null when nothing is unanalyzed or no chat download is in flight.
  quickBoundaryHighlight = computed(() => {
    if (!this.isChatDownloading()) return null;
    // Whole stream, not region-scoped: this is the download front, wherever the region is.
    const peaks = this.highlights().filter(h => !h.isAnalyzed && h.source === 'peak');
    if (peaks.length === 0) return null;
    let boundary = peaks[0];
    for (const h of peaks) {
      if (h.endTime > boundary.endTime) boundary = h;
    }
    return boundary;
  });

  // Count of unanalyzed peaks that Quick can safely send to Gemini RIGHT NOW. During
  // a download this excludes `quickBoundaryHighlight`; with download finished this
  // matches `unanalyzedCount`. The Quick button gates off this value, not the raw
  // unanalyzedCount, so it stays clickable mid-download as long as there's at least
  // one settled cluster behind the front.
  analyzableQuickCount = computed(() => {
    const total = this.unanalyzedCount();
    // Parallel phase: every window has a growing front, no single boundary cluster
    // is "settled". Easier to gate Quick entirely until the orchestrator finishes.
    if (this.parallelDownloadActive()) return 0;
    if (!this.isChatDownloading()) return total;
    const boundary = this.quickBoundaryHighlight();
    const region = this.activeRegion();
    const boundaryCounted = !!boundary && (!region || overlapsRegion(boundary.startTime, boundary.endTime, region));
    return Math.max(0, total - (boundaryCounted ? 1 : 0));
  });

  canMerge = computed(() => this.selectedToMerge().size > 1);

  constructor() {
    this.loadStateFromLocalStorage();
    this.loadSearchHistory();

    // Auto-save state to localStorage whenever it changes
    effect(() => {
      // Always save scale even if not processed
      this.saveStateToLocalStorage();
    });

    // Search history is small and shared across VODs — хранится отдельным ключом,
    // чтобы не раздувать основной session-JSON и не терятся при смене проекта.
    effect(() => {
      const history = this.searchHistory();
      try { localStorage.setItem(this.SEARCH_HISTORY_KEY, JSON.stringify(history)); }
      catch { /* quota / disabled storage — ignore */ }
    });

    // ---- Cloud sync effects (no-op for non-Pro users; SyncService gates) ----

    // Push global settings to /settings/global on change.
    // Hash-gate avoids the spurious post-pull push: when apply-remote-settings
    // writes signals from a freshly-pulled cloud state, this effect re-fires
    // with the SAME values it just received — without the gate it would
    // immediately schedule a redundant write back to Firestore.
    let lastSettingsJson = '';
    effect(() => {
      const settings = {
        interfaceScale: this.interfaceScale(),
        isChatOverlayEnabled: this.isChatOverlayEnabled(),
        chatLayoutPreference: this.chatLayoutPreference(),
        heatmapScale: this.heatmapScale(),
        searchHistory: this.searchHistory(),
        sortOrder: this.sortOrder(),
      };
      const json = JSON.stringify(settings);
      if (json === lastSettingsJson) return;
      lastSettingsJson = json;
      this.syncService.pushSettings(settings);
    });

    // Push per-project state (highlights, bins, AI cache, ...) on change.
    // chatComments is intentionally not in this dependency graph — too large.
    // Hash-gate per videoId: only a real change since the copy was loaded pushes.
    // The first emission after a load is that copy itself → baseline, no push
    // (before 2026-10-12 every VOD open pushed the local copy 30s later and
    // replaced whatever another device had put in the cloud).
    effect(() => {
      const videoId = this.videoId();
      if (!videoId) return;
      if (!this.isProcessed()) return; // skip empty pre-analysis state
      const state = this.buildSyncPayload();
      const key = String(videoId);
      const sig = syncSignature(state);
      untracked(() => {
        if (this.syncBaselineFor === key) {
          this.syncBaselineFor = null;
          this.lastSyncSigByVideo.set(key, sig);
          return;
        }
        if (sig === this.lastSyncSigByVideo.get(key)) return;
        this.lastSyncSigByVideo.set(key, sig);
        if (this.reconcileHoldFor === key) {
          this.editedDuringHold = true;
          return;
        }
        this.syncDirty.set(true);
        this.syncService.pushProjectState(key, { ...state, syncRev: this.syncRev() });
      });
    });

    // A state write landed: remember the new cloud rev on the copy it came from.
    this.syncService.onStatePushed = (videoId, rev, stillPending) => {
      if (String(this.videoId()) === videoId) {
        this.syncRev.set(rev);
        if (!stillPending) this.syncDirty.set(false);
      } else {
        void this.patchSavedSyncMeta(videoId, rev, stillPending);
      }
    };

    // The VOD restored from the last session was loaded before sign-in — check it
    // against the cloud once the first pull for this user is done.
    effect(() => {
      if (!this.syncService.hasPulled()) return;
      untracked(() => void this.reconcileOpenVodWithCloud('boot'));
    });

    // Tab came back (other device may have edited meanwhile) → re-check the open VOD.
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') void this.reconcileOpenVodWithCloud('visible');
      });
    }

    // Apply pulled settings from cloud (one-shot per login).
    effect(() => {
      const remote = this.syncService.remoteSettings();
      if (remote === null) return;
      untracked(() => {
        if (typeof remote.interfaceScale === 'number') this.interfaceScale.set(remote.interfaceScale);
        if (typeof remote.isChatOverlayEnabled === 'boolean') this.isChatOverlayEnabled.set(remote.isChatOverlayEnabled);
        if (remote.chatLayoutPreference === 'auto' || remote.chatLayoutPreference === 'overlay' || remote.chatLayoutPreference === 'side') this.chatLayoutPreference.set(remote.chatLayoutPreference);
        if (remote.heatmapScale === 'log' || remote.heatmapScale === 'linear') this.heatmapScale.set(remote.heatmapScale);
        if (Array.isArray(remote.searchHistory)) this.searchHistory.set(remote.searchHistory.slice(0, this.SEARCH_HISTORY_MAX));
        if (remote.sortOrder === 'time' || remote.sortOrder === 'count') this.sortOrder.set(remote.sortOrder);
      });
    });
  }

  /** The part of the open VOD's state that syncs (no chat). Reads signals → tracked. */
  private buildSyncPayload(): SavedState {
    return {
      fileName: this.fileName(),
      activityBins: this.activityBins(),
      highlights: this.highlights(),
      maxActivity: this.maxActivity(),
      streamerName: this.streamerName(),
      videoTitle: this.videoTitle(),
      videoGame: this.videoGame(),
      videoId: this.videoId(),
      streamCreatedAt: this.streamCreatedAt(),
      peakMessageThreshold: this.peakMessageThreshold(),
      rewindOffsetSeconds: this.rewindOffsetSeconds(),
      isAnalyzed: this.isAnalyzed(),
      currentHighlightIndex: this.currentHighlightIndex(),
      sortOrder: this.sortOrder(),
      analysisCache: Array.from(this.analysisCache().entries()),
      // highlightStateCache is a plain Map (not a signal); read it untracked.
      highlightStateCache: untracked(() => Array.from(this.highlightStateCache.entries())),
      filterSettings: this.filterSettings(),
      analysisRegion: this.regionRange(),
      analysisRegionEnabled: this.regionEnabled(),
    };
  }

  // ============================================================
  // Cloud Sync — which copy of a VOD to show (utils/sync-reconcile.ts)
  // ============================================================

  /**
   * Pick the copy to open: local IDB vs cloud (Pro). Before 2026-10-12 a local
   * copy always won and the cloud was read only when there was none, so a device
   * that had opened a VOD earlier never saw edits made on another device.
   */
  async resolveProjectForOpen(videoId: string): Promise<OpenedProject> {
    const local: SavedState | null = (await this.libraryService.getProjectState(videoId)) ?? null;
    if (!this.syncService.isEligible()) return { videoId, state: local, decision: null, local, remote: null };

    let remote: RemoteState | null;
    try {
      remote = await this.syncService.pullProjectState(videoId);
    } catch (err) {
      // Unreachable ≠ absent. Open the local copy; a fresh download (no local copy)
      // gets syncRev NEW_DOC_REV and won't be allowed to replace a cloud doc.
      console.warn(`Cloud copy of ${videoId} unavailable, opening local:`, err);
      return { videoId, state: local, decision: null, local, remote: null };
    }

    const decision = decideOnOpen(local, remote);
    let state: SavedState | null = null;
    switch (decision.kind) {
      case 'remote':
        state = withLocalChat(remote!.state, local);
        break;
      case 'local':
        state = { ...local!, syncRev: decision.baseRev };
        break;
      case 'conflict':
        // Keep the old base: edits made before the user picks a side must keep
        // failing the push guard instead of overwriting the other device's work.
        state = local;
        break;
    }
    return { videoId, state, decision, local, remote };
  }

  /** Call right after loadStateObject(opened.state): pushes / raises a conflict if needed. */
  finishOpen(opened: OpenedProject): void {
    const d = opened.decision;
    if (!d || String(this.videoId()) !== opened.videoId) return;
    if (d.kind === 'local' && d.push) this.pushOpenVodNow();
    if (d.kind === 'conflict') {
      this.syncDirty.set(true);
      this.syncService.addConflict({ videoId: opened.videoId, local: opened.local!, remote: opened.remote! });
    }
  }

  /**
   * Same decision for the VOD that is already open: on sign-in ('boot' — the VOD
   * restored from the last session) and when the tab becomes visible again.
   * Silently swaps in a newer cloud copy.
   */
  private async reconcileOpenVodWithCloud(mode: 'boot' | 'visible'): Promise<void> {
    if (mode === 'visible') {
      if (this.reconcileHoldFor !== null) return; // boot check still pending
      if (Date.now() - this.lastReconcileAt < 10_000) return;
    }
    const vid = this.videoId();
    const key = vid ? String(vid) : null;
    // A write in progress (never the held boot VOD — its pushes are held) is left
    // to the push guard: swapping copies under it would raise a needless conflict.
    if (!key || !this.syncService.isEligible() || !this.isProcessed() || this.isChatDownloading()
        || this.syncService.hasPendingState(key)) {
      if (mode === 'boot') this.releaseReconcileHold();
      return;
    }
    this.lastReconcileAt = Date.now();

    let remote: RemoteState | null;
    try {
      remote = await this.syncService.pullProjectState(key);
    } catch {
      // Offline — held edits go out anyway; the push guard still protects the cloud.
      if (mode === 'boot') this.releaseReconcileHold();
      return;
    }
    // The world may have moved while we waited.
    if (String(this.videoId()) !== key || this.isChatDownloading() || this.syncService.hasPendingState(key)) {
      if (mode === 'boot') this.releaseReconcileHold();
      return;
    }

    const local = this.snapshotState();
    const d = decideOnOpen(local, remote);
    if (d.kind === 'remote') {
      const prevIndex = this.currentHighlightIndex();
      this.loadStateObject(withLocalChat(remote!.state, local), 'cloud'); // also drops the hold
      if (prevIndex < this.highlights().length) this.currentHighlightIndex.set(prevIndex);
      this.infoMessage.set('Стрим обновлён: подтянуты изменения с другого устройства');
    } else if (d.kind === 'local') {
      this.syncRev.set(d.baseRev);
      this.releaseReconcileHold(d.push);
    } else if (d.kind === 'conflict') {
      this.releaseReconcileHold(false, true);
      this.syncService.addConflict({ videoId: key, local, remote: remote! });
    }
  }

  /** End the boot hold; push if the copy is ahead or was edited meanwhile. */
  private releaseReconcileHold(push = false, keepEditsLocal = false): void {
    const held = this.reconcileHoldFor;
    const edited = this.editedDuringHold;
    this.reconcileHoldFor = null;
    this.editedDuringHold = false;
    if (keepEditsLocal) {
      if (edited) this.syncDirty.set(true);
      return;
    }
    if ((push || edited) && held !== null && held === String(this.videoId())) this.pushOpenVodNow();
  }

  /** Push the open VOD now (local copy is ahead of the cloud). */
  private pushOpenVodNow(): void {
    const vid = this.videoId();
    if (!vid) return;
    const state = untracked(() => this.buildSyncPayload());
    this.lastSyncSigByVideo.set(String(vid), syncSignature(state));
    this.syncDirty.set(true);
    this.syncService.pushProjectState(String(vid), { ...state, syncRev: this.syncRev() }, true);
  }

  /** User picked a side in the conflict modal. */
  async resolveSyncConflict(choice: 'local' | 'remote'): Promise<void> {
    const c = this.syncService.conflictDetected();
    if (!c) return;
    this.syncService.dropConflict(c.videoId);
    const isOpen = String(this.videoId()) === c.videoId;

    if (choice === 'local') {
      // Overwrite the cloud: re-base on its current rev and push right away.
      if (isOpen) {
        this.syncRev.set(c.remote.rev);
        this.pushOpenVodNow();
      } else {
        this.syncService.pushProjectState(c.videoId, { ...c.local, syncRev: c.remote.rev }, true);
      }
      return;
    }

    this.syncService.cancelPendingState(c.videoId);
    if (isOpen) {
      const prevIndex = this.currentHighlightIndex();
      this.loadStateObject(withLocalChat(c.remote.state, this.snapshotState()), 'cloud');
      if (prevIndex < this.highlights().length) this.currentHighlightIndex.set(prevIndex);
    } else {
      this.onDocumentEvent?.({ kind: 'replaced', videoId: c.videoId });
      const saved: SavedState | undefined = await this.libraryService.getProjectState(c.videoId);
      await this.libraryService.saveProjectState(c.videoId, withLocalChat(c.remote.state, saved));
    }
  }

  /** A write of a VOD that is no longer open landed — record its rev in IDB. */
  private async patchSavedSyncMeta(videoId: string, rev: string, stillPending: boolean): Promise<void> {
    try {
      const saved: SavedState | undefined = await this.libraryService.getProjectState(videoId);
      if (!saved) return;
      await this.libraryService.saveProjectState(videoId, {
        ...saved,
        syncRev: rev,
        syncDirty: stillPending ? saved.syncDirty : false,
      });
    } catch (e) {
      console.warn('Failed to record sync rev for', videoId, e);
    }
  }

  private loadSearchHistory(): void {
    try {
      const raw = localStorage.getItem(this.SEARCH_HISTORY_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        this.searchHistory.set(parsed.filter(x => typeof x === 'string').slice(0, this.SEARCH_HISTORY_MAX));
      }
    } catch { /* corrupt value — ignore */ }
  }

  pushSearchHistory(query: string): void {
    const q = query.trim();
    if (!q) return;
    this.searchHistory.update(list => {
      const deduped = list.filter(x => x !== q);
      deduped.unshift(q);
      return deduped.slice(0, this.SEARCH_HISTORY_MAX);
    });
  }

  resetState(): void {
    this.onDocumentEvent?.({ kind: 'reset' });
    localStorage.removeItem(this.SESSION_STORAGE_KEY);
    this.error.set('');
    this.infoMessage.set('');
    this.progressInfo.set(null);
    this.fileName.set('');
    this.activityBins.set([]);
    this.chatComments.set([]);
    this.filteredComments.set([]);
    this.highlights.set([]);
    this.maxActivity.set(0);
    this.currentHighlightIndex.set(0);
    this.streamerName.set(null);
    this.videoTitle.set(null);
    this.videoGame.set(null);
    this.videoId.set(null);
    this.streamCreatedAt.set(null);
    this.broadcasterId.set(null);
    this.streamerLogin.set(null);
    this.isAnalyzed.set(false);
    this.isProcessed.set(false);
    this.chatDownloadComplete.set(true);
    this.chatDownloadCursor.set(null);
    this.isLive.set(false);
    this.liveStreamId.set(null);
    this.lastLivePollAt.set(null);
    this.liveDurationSec.set(0);
    this.sortOrder.set('time');
    this.activeTab.set('all');
    this.selectedToMerge.set(new Set());
    this.searchQuery.set('');
    this.searchExactMatch.set(false);
    this.searchBinMatches.set(new Map());
    // searchHistory НЕ сбрасываем — она общая между проектами
    this.analysisCache.set(new Map());
    this.highlightStateCache.clear();
    this.filterSettings.set({ hideSubs: false, keywords: [], ignoredUsers: [] });
    this.regionRange.set(null);
    this.regionEnabled.set(false);
    this.regionDraft.set(null);
    // Whatever comes next is built here from scratch (fresh chat download): its
    // first push may create the cloud doc but never replace an existing one.
    this.syncRev.set(NEW_DOC_REV);
    this.syncDirty.set(false);
    this.syncBaselineFor = null;
    this.reconcileHoldFor = null;
    this.editedDuringHold = false;

    // Keep scale setting
    // this.interfaceScale.set(100); 

    // CSV state
    this.isCsvMode.set(false);
    this.pendingCsvContent.set(null);
    this.csvVodId.set('');
    this.csvStreamerName.set('');
    this.csvVideoTitle.set('');
    this.csvVideoGame.set('');
  }

  private loadStateFromLocalStorage(): void {
    try {
      const savedStateJSON = localStorage.getItem(this.SESSION_STORAGE_KEY);

      if (!savedStateJSON || savedStateJSON === 'undefined' || savedStateJSON === 'null') {
        if (savedStateJSON) {
          console.warn('Found corrupted state (undefined/null string), clearing localStorage.');
          localStorage.removeItem(this.SESSION_STORAGE_KEY);
        }
        return;
      }

      const cleanedJSON = savedStateJSON.trim();

      if (cleanedJSON === "" || cleanedJSON === "undefined" || cleanedJSON === "null") {
        console.warn('Found corrupted trimmed state, clearing localStorage.');
        localStorage.removeItem(this.SESSION_STORAGE_KEY);
        return;
      }

      this.isLoading.set(true);
      this.loadingMessage.set('Восстановление предыдущей сессии...');

      const savedState: SavedState = JSON.parse(cleanedJSON);

      this.fileName.set(savedState.fileName || '');
      this.activityBins.set(savedState.activityBins || []);
      // Load raw comments if available (might be large, but necessary for overlay)
      this.chatComments.set(savedState.chatComments || []);
      // If we have saved raw comments but no filtered, initially assume filtered = raw
      this.filteredComments.set(savedState.chatComments || []);

      // Backwards compatibility for old data models
      const mappedHighlights = (savedState.highlights || []).map((h: any) => {
        if (h.description === undefined) {
          h.description = h.summary || h.possibleEvent || 'Отсутствует описание';
        }
        return h as Highlight;
      });
      this.highlights.set(mappedHighlights);
      this.maxActivity.set(savedState.maxActivity || 0);
      this.streamerName.set(savedState.streamerName || null);
      this.videoTitle.set(savedState.videoTitle || null);
      this.videoGame.set(savedState.videoGame || null);
      this.videoId.set(savedState.videoId || null);
      this.streamCreatedAt.set(savedState.streamCreatedAt || null);
      this.peakMessageThreshold.set(savedState.peakMessageThreshold || 15);
      this.rewindOffsetSeconds.set(savedState.rewindOffsetSeconds || 15);
      this.isAnalyzed.set(savedState.isAnalyzed || false);
      this.currentHighlightIndex.set(savedState.currentHighlightIndex || 0);
      this.sortOrder.set(savedState.sortOrder || 'time');
      this.isChatOverlayEnabled.set(savedState.isChatOverlayEnabled ?? true);
      this.chatLayoutPreference.set(savedState.chatLayoutPreference || 'auto');
      this.interfaceScale.set(savedState.interfaceScale || 100);
      this.heatmapScale.set(savedState.heatmapScale === 'linear' ? 'linear' : 'log');
      // Resume markers — undefined on legacy states means "treat as complete" (we
      // don't want to spuriously resume already-finished projects from before the
      // auto-resume feature shipped).
      this.chatDownloadComplete.set(savedState.chatDownloadComplete ?? true);
      this.chatDownloadCursor.set(savedState.chatDownloadCursor ?? null);
      // Live hints — optimistic restore so the badge shows immediately on reopen,
      // BUT only if the chat download is still partial. complete=true means the
      // live-poll loop exited cleanly (stream ended OR safeguard) and set
      // isLive=false right before the save; a stale isLive=true with complete=true
      // would otherwise pin a permanent red badge on a finalized VOD until the
      // user re-opens it (no /get-vod-status check fires for complete VODs).
      const liveCandidate = (savedState.isLive ?? false) && savedState.chatDownloadComplete === false;
      this.isLive.set(liveCandidate);
      this.liveStreamId.set(savedState.liveStreamId ?? null);
      this.lastLivePollAt.set(null); // wallclock — never persisted
      this.liveDurationSec.set(0);   // re-populated by next /get-vod-status response
      this.applyLoadedSyncMeta(savedState);
      // Restored before sign-in: hold its pushes until it's checked against the cloud.
      if (savedState.videoId) this.reconcileHoldFor = String(savedState.videoId);

      if (savedState.filterSettings) {
        this.filterSettings.set(savedState.filterSettings);
      }
      this.applyLoadedRegion(savedState);

      if (savedState.analysisCache) {
        // Backwards compatibility for old cache
        const mappedCache = new Map<number, AnalysisCacheData>();
        for (const [key, value] of savedState.analysisCache) {
          const v = value as any;
          if (v.description === undefined) {
            v.description = v.summary || v.possibleEvent || 'Отсутствует описание';
          }
          mappedCache.set(Number(key), v as AnalysisCacheData);
        }
        this.analysisCache.set(mappedCache);
      }
      if (savedState.highlightStateCache) {
        this.highlightStateCache = new Map(savedState.highlightStateCache);
      }

      if (this.activityBins().length > 0) {
        this.isProcessed.set(true);
      }

      // localStorage may have held the "lite" version (chatComments stripped
      // due to the 5-10 MB browser quota — see saveStateToLocalStorage).
      // IDB has full state, so async-fetch it and overlay chat data. Without
      // this, page refresh on big chats showed the "Скачать чат" CTA even
      // though chat WAS persisted, frustrating the user.
      const vid = this.videoId();
      if (vid && this.chatComments().length === 0 && this.activityBins().length > 0) {
        this.libraryService.getProjectState(String(vid)).then((idbState: SavedState | undefined) => {
          if (!idbState) return;
          const fullChat = idbState.chatComments;
          if (Array.isArray(fullChat) && fullChat.length > 0) {
            this.chatComments.set(fullChat);
            // Approximation — filteredComments should match current
            // filterSettings, but recomputing requires TwitchChatService
            // which would create a circular import. If filters are active,
            // user can toggle them once to refresh; the bins below already
            // reflect filtered counts from the previous save.
            this.filteredComments.set(fullChat);
          }
          if (Array.isArray(idbState.activityBins) && idbState.activityBins.length > 0) {
            this.activityBins.set(idbState.activityBins);
          }
        }).catch((e: any) => console.warn('Failed to hydrate chat from IDB', e));
      }
    } catch (e) {
      console.error("Failed to parse saved state from localStorage", e);
      localStorage.removeItem(this.SESSION_STORAGE_KEY);
    } finally {
      this.isLoading.set(false);
    }
  }

  /** `origin` 'cloud' = the copy came from the cloud (another device) — drops the VOD's undo history. */
  loadStateObject(savedState: SavedState, origin: 'local' | 'cloud' = 'local'): void {
    this.isLoading.set(true);
    this.loadingMessage.set('Восстановление сохраненного проекта...');

    this.fileName.set(savedState.fileName || '');
    this.activityBins.set(savedState.activityBins || []);
    this.chatComments.set(savedState.chatComments || []);
    this.filteredComments.set(savedState.chatComments || []);

    // Create analysis cache first so we can map it to highlights
    const loadedAnalysisCache = new Map<number, AnalysisCacheData>();
    if (savedState.analysisCache) {
      for (const [key, value] of savedState.analysisCache) {
        const v = value as any;
        if (v.description === undefined) {
          v.description = v.summary || v.possibleEvent || 'Отсутствует описание';
        }
        loadedAnalysisCache.set(Number(key), v as AnalysisCacheData);
      }
      this.analysisCache.set(loadedAnalysisCache);
    } else {
      this.analysisCache.set(new Map());
    }

    // Map highlights and rehydrate AI analysis from cache if present
    const mappedHighlights = (savedState.highlights || []).map((h: any) => {
      const hl = h as Highlight;

      const cachedAi = loadedAnalysisCache.get(hl.startTime);
      if (cachedAi && hl.source === 'peak') {
        hl.description = cachedAi.description;
        hl.sentiment = cachedAi.sentiment;
        hl.isAnalyzed = true;
      } else if (hl.description === undefined) {
        hl.description = h.summary || h.possibleEvent || 'Отсутствует описание';
      }
      return hl;
    });
    this.highlights.set(mappedHighlights);
    this.maxActivity.set(savedState.maxActivity || 0);
    this.streamerName.set(savedState.streamerName || null);
    this.videoTitle.set(savedState.videoTitle || null);
    this.videoGame.set(savedState.videoGame || null);
    // broadcasterId isn't in SavedState: drop the previous VOD's one, else the clips
    // Helix fallback asked for another channel's clips (all filtered out → empty КЛИПЫ).
    if (String(this.videoId()) !== String(savedState.videoId)) {
      this.broadcasterId.set(null);
      this.streamerLogin.set(null);
    }
    this.videoId.set(savedState.videoId || null);
    this.streamCreatedAt.set(savedState.streamCreatedAt || null);
    this.peakMessageThreshold.set(savedState.peakMessageThreshold || 15);
    this.rewindOffsetSeconds.set(savedState.rewindOffsetSeconds || 15);
    this.isAnalyzed.set(savedState.isAnalyzed || false);
    this.currentHighlightIndex.set(savedState.currentHighlightIndex || 0);
    this.sortOrder.set(savedState.sortOrder || 'time');
    this.isChatOverlayEnabled.set(savedState.isChatOverlayEnabled ?? true);
    this.chatLayoutPreference.set(savedState.chatLayoutPreference || 'auto');
    this.interfaceScale.set(savedState.interfaceScale || 100);
    this.heatmapScale.set(savedState.heatmapScale === 'linear' ? 'linear' : 'log');
    this.chatDownloadComplete.set(savedState.chatDownloadComplete ?? true);
    this.chatDownloadCursor.set(savedState.chatDownloadCursor ?? null);
    // Same guard as loadStateFromLocalStorage: a stale isLive=true with
    // chatDownloadComplete=true would pin a red badge on an already-finalized VOD.
    const liveCandidate = (savedState.isLive ?? false) && savedState.chatDownloadComplete === false;
    this.isLive.set(liveCandidate);
    this.liveStreamId.set(savedState.liveStreamId ?? null);
    this.lastLivePollAt.set(null);
    this.liveDurationSec.set(0);
    this.applyLoadedSyncMeta(savedState);

    if (savedState.filterSettings) {
      this.filterSettings.set(savedState.filterSettings);
    }
    this.applyLoadedRegion(savedState);

    // (Analysis Cache was already handled above)
    if (savedState.highlightStateCache) {
      this.highlightStateCache = new Map(savedState.highlightStateCache);
    }

    if (this.activityBins().length > 0) {
      this.isProcessed.set(true);
    }
    this.isLoading.set(false);
    this.onDocumentEvent?.({ kind: 'loaded', videoId: String(savedState.videoId ?? ''), origin });
  }

  /**
   * Force-save the current state to IDB + localStorage, bypassing the
   * isChatDownloading guard. Use at supersede points (new loadFromUrl over an
   * active one) so the in-memory partial chatComments + cursor are persisted
   * for next-open auto-resume — without this snapshot, the imminent resetState
   * clobbers chat data that hadn't yet been written through the gated autosave.
   */
  saveStateForResume(): void {
    this.saveStateToLocalStorage(true);
  }

  /** Region of the copy being loaded — malformed / missing reads as «no region, off». */
  private applyLoadedRegion(savedState: SavedState): void {
    const range = parseRegion(savedState.analysisRegion);
    this.regionRange.set(range);
    this.regionEnabled.set(!!range && savedState.analysisRegionEnabled === true);
    this.regionDraft.set(null);
  }

  /** Sync bookkeeping of a copy being loaded; its first push-effect emission is the baseline. */
  private applyLoadedSyncMeta(savedState: SavedState): void {
    this.syncRev.set(savedState.syncRev);
    this.syncDirty.set(!!savedState.syncDirty);
    this.syncBaselineFor = savedState.videoId ? String(savedState.videoId) : null;
    this.reconcileHoldFor = null;
    this.editedDuringHold = false;
  }

  /** Full in-memory state of the open VOD, chat included (what IDB stores). */
  private snapshotState(): SavedState {
    return {
      fileName: this.fileName(),
      activityBins: this.activityBins(),
      chatComments: this.chatComments(), // Save comments
      highlights: this.highlights(),
      maxActivity: this.maxActivity(),
      streamerName: this.streamerName(),
      videoTitle: this.videoTitle(),
      videoGame: this.videoGame(),
      videoId: this.videoId(),
      streamCreatedAt: this.streamCreatedAt(),
      peakMessageThreshold: this.peakMessageThreshold(),
      rewindOffsetSeconds: this.rewindOffsetSeconds(),
      isAnalyzed: this.isAnalyzed(),
      currentHighlightIndex: this.currentHighlightIndex(),
      sortOrder: this.sortOrder(),
      analysisCache: Array.from(this.analysisCache().entries()),
      highlightStateCache: Array.from(this.highlightStateCache.entries()),
      isChatOverlayEnabled: this.isChatOverlayEnabled(),
      chatLayoutPreference: this.chatLayoutPreference(),
      interfaceScale: this.interfaceScale(),
      heatmapScale: this.heatmapScale(),
      filterSettings: this.filterSettings(),
      analysisRegion: this.regionRange(),
      analysisRegionEnabled: this.regionEnabled(),
      chatDownloadComplete: this.chatDownloadComplete(),
      chatDownloadCursor: this.chatDownloadCursor(),
      isLive: this.isLive(),
      liveStreamId: this.liveStreamId(),
      syncRev: this.syncRev(),
      syncDirty: this.syncDirty(),
    };
  }

  private saveStateToLocalStorage(force: boolean = false): void {
    // Demo mode: never persist demo data to the real user's localStorage / IDB.
    if (checkDemoMode()) return;
    // Skip during streaming chat download — chatComments mutates rapidly via onBatch
    // (every GQL page = one update), and serializing the full ~10MB state on every
    // page would saturate IDB + localStorage. Reading isChatDownloading first keeps
    // the autosave effect subscribed; the moment download finishes (flag → false)
    // the effect re-fires and persists the conclusive state in one shot.
    // `force=true` overrides this gate at supersede / unload checkpoints.
    if (!force && this.isChatDownloading()) return;
    try {
      const stateToSave = this.snapshotState();

      if (stateToSave) {
        // IDB is the source of truth — has effectively unlimited (1GB+) quota
        // and stores chatComments + bin.messages full. Save first so a
        // later localStorage failure can't lose data.
        if (this.videoId()) {
          this.libraryService.saveProjectState(String(this.videoId()), stateToSave).catch(e => {
            console.error('Failed to save state to Library IDB', e);
          });
        }

        // localStorage cap is ~5-10 MB. xQc-tier chats (chatComments ~10MB +
        // bin.messages ~10MB duplicate) overflow. Try full first; on
        // QuotaExceededError fall back to a "lite" version without raw chat
        // and without bin.messages — page-refresh restores metadata fast,
        // and the existing needsChatRedownload CTA prompts re-download (or
        // user re-opens the VOD via library, which loads full state from IDB).
        const json = JSON.stringify(stateToSave);
        if (json && json !== "undefined" && json !== "null") {
          try {
            localStorage.setItem(this.SESSION_STORAGE_KEY, json);
          } catch (qe) {
            const isQuota = qe instanceof DOMException && (
              qe.name === 'QuotaExceededError' ||
              qe.name === 'NS_ERROR_DOM_QUOTA_REACHED' // Firefox legacy
            );
            if (!isQuota) {
              console.error("localStorage save failed (non-quota)", qe);
              return;
            }
            const liteState: SavedState = {
              ...stateToSave,
              chatComments: [],
              activityBins: (stateToSave.activityBins || []).map(b => ({
                startTime: b.startTime,
                messageCount: b.messageCount,
                messages: [],
              })),
            };
            try {
              localStorage.setItem(this.SESSION_STORAGE_KEY, JSON.stringify(liteState));
              console.warn('localStorage: chat too big for full save, stored lite version (chat in IDB)');
            } catch (qe2) {
              console.warn('localStorage: even lite save failed, skipping', qe2);
            }
          }
        }
      }
    } catch (e) {
      console.error("Failed to save state to localStorage", e);
    }
  }
}
